import { isEqual, isFunction } from "lodash";
import React, {
  createContext,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Socket } from "socket.io-client";
import { ConfirmDialogType } from "../../../types/global";
import { ChatMessage, SelectedTabType } from "../types";
import {
  initialDialogs,
  InitialNavigatePage,
  NavigatePageType,
} from "../types/ChatsUiHelper";

export type SocketContextType = Socket | null | undefined;

export interface ChatsContextInterface {
  navigatePage: NavigatePageType;
  setNavigatePage: (nextSelectedTab: SetStateAction<NavigatePageType>) => void;
  confirmDialog: ConfirmDialogType;
  setConfirmDialog: (
    nextConfirmDialog: SetStateAction<ConfirmDialogType>
  ) => void;
  selectedTab: SelectedTabType;
  setSelectedTab: (nextSelectedTab: SetStateAction<SelectedTabType>) => void;
  replyingTo: ChatMessage;
  setReplyingTo: (nextSelectedTab: SetStateAction<ChatMessage>) => void;
  newChatActionsShow: boolean;
  setNewChatActionsShow: (nextSelectedTab: SetStateAction<boolean>) => void;
  resetContext: () => void;
}

interface ChatsUiContextInterface {
  children: React.ReactNode;
}

const ChatsUIContext = createContext<ChatsContextInterface | null>(null);
const ChatsUIConsumer = ChatsUIContext.Consumer;

const useChatsUIContext = () => {
  return useContext<ChatsContextInterface | null>(ChatsUIContext);
};

const ChatsUIProvider: React.FC<ChatsUiContextInterface> = ({ children }) => {
  const [navigatePage, setNavigatePage] =
    useState<NavigatePageType>(InitialNavigatePage);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogType>(
    initialDialogs as ConfirmDialogType
  );
  const [selectedTab, setSelectedTab] = useState<SelectedTabType>("home");
  const [replyingTo, setReplyingTo] = useState<ChatMessage>({} as ChatMessage);
  const [newChatActionsShow, setNewChatActionsShow] = useState<boolean>(false);

  const setNavigatePageBase = useCallback(
    (nextNavigatePage: SetStateAction<NavigatePageType>) => {
      setNavigatePage((prevNavigatePage) => {
        if (isFunction(nextNavigatePage)) {
          nextNavigatePage = nextNavigatePage(prevNavigatePage);
        }

        if (isEqual(nextNavigatePage, prevNavigatePage)) {
          return prevNavigatePage;
        }

        return nextNavigatePage;
      });
    },
    []
  );

  const setConfirmDialogBase = useCallback(
    (nextConfirm: SetStateAction<ConfirmDialogType>) => {
      setConfirmDialog((prevConfirm) => {
        if (isFunction(nextConfirm)) {
          nextConfirm = nextConfirm(prevConfirm);
        }

        if (isEqual(nextConfirm, prevConfirm)) {
          return prevConfirm;
        }

        return nextConfirm;
      });
    },
    []
  );

  const setSelectedTabBase = useCallback(
    (nextSelectedTab: SetStateAction<SelectedTabType>) => {
      setSelectedTab((prevSelectedTab) => {
        if (isFunction(nextSelectedTab)) {
          nextSelectedTab = nextSelectedTab(prevSelectedTab);
        }

        if (isEqual(nextSelectedTab, prevSelectedTab)) {
          return prevSelectedTab;
        }

        return nextSelectedTab;
      });
    },
    []
  );

  const setReplyingToBase = useCallback(
    (nextReplyingTo: SetStateAction<ChatMessage>) => {
      setReplyingTo((prevReplyingTo) => {
        if (isFunction(nextReplyingTo)) {
          nextReplyingTo = nextReplyingTo(prevReplyingTo);
        }

        if (isEqual(nextReplyingTo, prevReplyingTo)) {
          return prevReplyingTo;
        }

        return nextReplyingTo;
      });
    },
    []
  );

  const setNewChatActionsShowBase = useCallback(
    (nextNewChatActionsShow: SetStateAction<boolean>) => {
      setNewChatActionsShow((prevNewChatActionsShow) => {
        if (isFunction(nextNewChatActionsShow)) {
          nextNewChatActionsShow = nextNewChatActionsShow(
            prevNewChatActionsShow
          );
        }

        if (isEqual(nextNewChatActionsShow, prevNewChatActionsShow)) {
          return prevNewChatActionsShow;
        }

        return nextNewChatActionsShow;
      });
    },
    []
  );

  const value: ChatsContextInterface = useMemo(() => {
    const resetContext = () => {
      setNavigatePageBase(InitialNavigatePage);
      setConfirmDialogBase(initialDialogs);
      setSelectedTabBase("home");
      setReplyingToBase({} as ChatMessage);
      setNewChatActionsShowBase(false);
    };

    return {
      navigatePage: navigatePage,
      setNavigatePage: setNavigatePageBase,
      confirmDialog: confirmDialog,
      setConfirmDialog: setConfirmDialogBase,
      selectedTab: selectedTab,
      setSelectedTab: setSelectedTabBase,
      replyingTo: replyingTo,
      setReplyingTo: setReplyingToBase,
      newChatActionsShow: newChatActionsShow,
      setNewChatActionsShow: setNewChatActionsShowBase,
      resetContext: resetContext,
    };
  }, [
    navigatePage,
    setNavigatePageBase,
    confirmDialog,
    setConfirmDialogBase,
    selectedTab,
    setSelectedTabBase,
    replyingTo,
    setReplyingToBase,
    newChatActionsShow,
    setNewChatActionsShowBase,
  ]);

  return (
    <ChatsUIContext.Provider value={value}>{children}</ChatsUIContext.Provider>
  );
};
export { ChatsUIConsumer, ChatsUIProvider, useChatsUIContext };
